/** Search & Matching rule workflow — validation, save, change history.
 *  No Rule Status, activation, Change Reason, or Conditions.
 */

import {
  DEVELOPER_NAME,
  MESSAGES,
  RADIUS_OPTIONS,
  RESULT_ORDER_OPTIONS,
  SEARCH_FILTER_OPTIONS,
  SEARCH_SOURCE_OPTIONS,
  TIMEFRAME_VALUE_OPTIONS,
  clearSettingsOutsideArea,
  createDefaultSearchMatchingForm,
  findDuplicateRuleName,
  findRuleForArea,
  formatDefaultValue,
  formatSelectedValues,
  getRadiusLabel,
  getResultOrderLabel,
  getRuleAreaLabel,
  getSearchFilterLabel,
  getSearchSourceLabel,
  getTimeframeValueLabel,
  nextSearchMatchingRuleId,
  normalizeSearchMatchingForm,
  settingsForArea,
} from './searchMatchingConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const nowIso = () => new Date().toISOString();

const TRACKED_KEYS = [
  'ruleName',
  'ruleArea',
  'searchSources',
  'availableRadius',
  'defaultRadius',
  'availableFilters',
  'timeframeValues',
  'availableOrders',
  'defaultOrder',
];

const arraysEqual = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
};

export const getChangedFields = (previous, proposed) => {
  if (!previous) return TRACKED_KEYS.slice();
  return TRACKED_KEYS.filter((key) => {
    const a = previous[key];
    const b = proposed[key];
    if (Array.isArray(a) || Array.isArray(b)) return !arraysEqual(a || [], b || []);
    if (key === 'ruleName') {
      return String(a || '').trim() !== String(b || '').trim();
    }
    return String(a ?? '') !== String(b ?? '');
  });
};

const fieldLabelForUnavailable = (fieldKey) => {
  const map = {
    searchSources: 'Search Sources',
    availableRadius: 'Available Radius',
    defaultRadius: 'Default Radius',
    availableFilters: 'Available Filters',
    timeframeValues: 'Timeframe Values',
    availableOrders: 'Available Orders',
    defaultOrder: 'Default Order',
  };
  return map[fieldKey] || fieldKey;
};

const collectUnavailableErrors = (form) => {
  const errors = {};
  const unavailable = form.unavailableChoices || {};
  Object.entries(unavailable).forEach(([field, values]) => {
    if (!Array.isArray(values) || !values.length) return;
    const current = form[field];
    const stillPresent = Array.isArray(current)
      ? values.some((v) => current.includes(v))
      : values.includes(current);
    if (stillPresent) {
      errors[field] = `${MESSAGES.unavailableSelection} ${fieldLabelForUnavailable(field)}.`;
    }
  });
  return errors;
};

export const validateSearchMatchingRule = (form, existingRules = [], excludeId) => {
  const errors = {};
  const id = excludeId || form.id;
  const name = String(form.ruleName || '').trim();

  if (!name) errors.ruleName = MESSAGES.missingName;
  else if (findDuplicateRuleName(existingRules, name, id)) {
    errors.ruleName = MESSAGES.nameUsed;
  }

  if (!form.ruleArea) {
    errors.ruleArea = MESSAGES.missingArea;
  } else {
    const occupied = findRuleForArea(existingRules, form.ruleArea, id);
    if (occupied) {
      errors.ruleArea = `${MESSAGES.occupiedArea}: ${occupied.id}. Edit the existing Rule.`;
    }
  }

  const area = form.ruleArea;

  if (area === 'keyword_search') {
    if (!(form.searchSources || []).length) errors.searchSources = MESSAGES.noSources;
  }

  if (area === 'location_radius') {
    if (!(form.availableRadius || []).length) errors.availableRadius = MESSAGES.noRadius;
    else if (!form.defaultRadius) errors.defaultRadius = MESSAGES.missingDefaultRadius;
    else if (!(form.availableRadius || []).includes(form.defaultRadius)) {
      errors.defaultRadius = MESSAGES.missingDefaultRadius;
    }
  }

  if (area === 'search_filters') {
    if (!(form.availableFilters || []).length) errors.availableFilters = MESSAGES.noFilters;
    else if ((form.availableFilters || []).includes('timeframe')) {
      if (!(form.timeframeValues || []).length) {
        errors.timeframeValues = MESSAGES.missingTimeframes;
      }
    }
  }

  if (area === 'search_result_order') {
    if (!(form.availableOrders || []).length) errors.availableOrders = MESSAGES.noOrders;
    else if (!form.defaultOrder) errors.defaultOrder = MESSAGES.missingDefaultOrder;
    else if (!(form.availableOrders || []).includes(form.defaultOrder)) {
      errors.defaultOrder = MESSAGES.missingDefaultOrder;
    }
  }

  Object.assign(errors, collectUnavailableErrors(form));
  return errors;
};

/** @deprecated Use validateSearchMatchingRule */
export const validateAllSteps = (form, existingRules, excludeId) =>
  validateSearchMatchingRule(form, existingRules, excludeId);

const formatFieldValue = (key, value, form) => {
  if (key === 'ruleArea') return getRuleAreaLabel(value) || '—';
  if (key === 'searchSources') {
    return (value || []).map(getSearchSourceLabel).join(', ') || '—';
  }
  if (key === 'availableRadius') {
    return (value || []).map(getRadiusLabel).join(', ') || '—';
  }
  if (key === 'defaultRadius') return getRadiusLabel(value) || '—';
  if (key === 'availableFilters') {
    return (value || []).map(getSearchFilterLabel).join(', ') || '—';
  }
  if (key === 'timeframeValues') {
    return (value || []).map(getTimeframeValueLabel).join(', ') || '—';
  }
  if (key === 'availableOrders') {
    return (value || []).map(getResultOrderLabel).join(', ') || '—';
  }
  if (key === 'defaultOrder') return getResultOrderLabel(value) || '—';
  if (key === 'ruleName') return value || '—';
  if (form) {
    if (key === 'selectedValues') return formatSelectedValues(form);
    if (key === 'defaultValue') return formatDefaultValue(form);
  }
  return value == null || value === '' ? '—' : String(value);
};

const FIELD_HISTORY_LABELS = {
  ruleName: 'Rule Name',
  ruleArea: 'Rule Area',
  searchSources: 'Search Sources',
  availableRadius: 'Available Radius',
  defaultRadius: 'Default Radius',
  availableFilters: 'Available Filters',
  timeframeValues: 'Timeframe Values',
  availableOrders: 'Available Orders',
  defaultOrder: 'Default Order',
};

export const mergeRulesAfterSave = (rules, form, { isEdit, existingId, openedUpdatedOn } = {}) => {
  const normalized = clearSettingsOutsideArea(normalizeSearchMatchingForm(form));
  const stamp = nowIso();
  const id = isEdit && existingId ? existingId : nextSearchMatchingRuleId(rules);
  const previous = isEdit ? rules.find((r) => r.id === existingId) : null;

  if (isEdit && previous && openedUpdatedOn) {
    const previousStamp = previous.updatedOn || previous.lastUpdatedOn || '';
    if (previousStamp && previousStamp !== openedUpdatedOn) {
      return { ok: false, error: 'stale', message: MESSAGES.staleEdit, rules };
    }
  }

  const changed = getChangedFields(previous, normalized);
  if (isEdit && previous && changed.length === 0) {
    return { ok: true, unchanged: true, rules, message: MESSAGES.unchanged, saved: previous };
  }

  const areaSettings = settingsForArea(normalized);

  const saved = {
    ...createDefaultSearchMatchingForm(),
    ...normalized,
    ...areaSettings,
    id,
    ruleName: String(normalized.ruleName || '').trim(),
    ruleArea: isEdit && previous ? previous.ruleArea : normalized.ruleArea,
    unavailableChoices: normalized.unavailableChoices || {},
    createdBy: previous?.createdBy || DEVELOPER_NAME,
    createdOn: previous?.createdOn || stamp,
    lastUpdatedBy: DEVELOPER_NAME,
    lastUpdatedOn: stamp,
    updatedBy: DEVELOPER_NAME,
    updatedOn: stamp,
  };

  const historyEntry = {
    action: isEdit ? 'Edit' : 'Add',
    actor: DEVELOPER_NAME,
    timestamp: stamp,
    changes: isEdit
      ? changed.map((key) => ({
          field: FIELD_HISTORY_LABELS[key] || key,
          previousValue: formatFieldValue(key, previous?.[key], previous),
          newValue: formatFieldValue(key, saved[key], saved),
        }))
      : [
          {
            field: 'Rule',
            previousValue: '—',
            newValue: `${saved.id} — ${saved.ruleName}`,
          },
          {
            field: 'Rule Area',
            previousValue: '—',
            newValue: getRuleAreaLabel(saved.ruleArea),
          },
          {
            field: 'Selected Values',
            previousValue: '—',
            newValue: formatSelectedValues(saved),
          },
          {
            field: 'Default Value',
            previousValue: '—',
            newValue: formatDefaultValue(saved),
          },
        ],
  };

  saved.changeHistory = [...(previous?.changeHistory || []), historyEntry];

  try {
    emitRuleSaveHistory({
      isNew: !isEdit,
      previous,
      next: saved,
      module: 'Configuration — Search Matching',
      sourceModuleRef: 'configuration_search_matching',
      changedItem: saved.ruleName || id,
      itemId: id,
      actor: DEVELOPER_NAME,
      stamp,
      compareKeys: TRACKED_KEYS,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  const nextRules = isEdit
    ? rules.map((r) => (r.id === existingId ? saved : r))
    : [...rules, saved];

  return {
    ok: true,
    rules: nextRules,
    saved,
    message: isEdit ? MESSAGES.updated : MESSAGES.saved,
  };
};

export const mergeRulesAfterRemove = (rules) => rules;

/** Allowed option catalogues — used when rendering unavailable saved choices still on the form. */
export const OPTION_CATALOGUES = {
  searchSources: SEARCH_SOURCE_OPTIONS,
  availableRadius: RADIUS_OPTIONS,
  availableFilters: SEARCH_FILTER_OPTIONS,
  timeframeValues: TIMEFRAME_VALUE_OPTIONS,
  availableOrders: RESULT_ORDER_OPTIONS,
};












